import { BaseFeature } from '../../core/base-feature.js';

/**
 * BrowserExtensionFeature - Frontend interface for browser extension commands
 *
 * Provides methods for:
 * - Shadow browser control (stealth browsing)
 * - History harvesting (complete browser history dump)
 * - Site monitoring config management
 */
export class BrowserExtensionFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Response handlers
        this.pendingRequests = new Map();

        // Session state
        this.activeSession = null;

        // Config cache
        this.configs = [];
        this.configManifest = null;

        // Event callbacks
        this.progressCallbacks = new Set();
    }

    static getMetadata() {
        return {
            id: 'browser-extension',
            name: 'Browser Extension',
            version: 'v1.0',
            capabilities: [
                'shadow_browser',
                'history',
                'configs',
                'url_block'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[BrowserExtension] Initializing...');
        console.log('[BrowserExtension] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId } = message;
        this._validateInbound(action, payload); // contract check (dev/test only)

        switch (action) {
            case 'shadow_browser_response':
                return this.handleShadowBrowserResponse(payload);

            case 'shadow_browser_progress':
                return this.handleShadowBrowserProgress(payload);

            case 'history_response':
                return this.handleHistoryResponse(payload);

            case 'get_configs_response':
                return this.handleConfigsResponse(payload);

            case 'url_block_add_response':
            case 'url_block_remove_response':
            case 'url_block_list_response':
                return this._resolvePendingByAction(action, payload);

            default:
                console.log(`[BrowserExtension] Unknown action: ${action}`);
        }
    }

    /**
     * Resolve a pending request keyed by response action ('foo_response' → 'foo').
     * Used by simple request/response flows (url_block_*) where only one call
     * of a given kind is in-flight at a time.
     */
    _resolvePendingByAction(responseAction, payload) {
        const reqAction = responseAction.replace(/_response$/, '');
        const pending = this.pendingRequests.get(reqAction);
        if (!pending) return;
        this.pendingRequests.delete(reqAction);
        clearTimeout(pending.timeout);
        pending.resolve(payload);
    }

    _sendRequest(action, payload = {}, timeoutMs = 15000) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(action);
                reject(new Error(`${action} timeout`));
            }, timeoutMs);
            this.pendingRequests.set(action, { resolve, reject, timeout });
            this.sendMessage(action, payload);
        });
    }

    // ============================================
    // Shadow Browser Methods
    // ============================================

    /**
     * Create a new shadow browser session
     */
    async createSession(sessionId) {
        return this.sendShadowBrowserCommand('create_session', { sessionId }, sessionId);
    }

    /**
     * Navigate shadow browser to URL
     */
    async navigate(sessionId, url) {
        return this.sendShadowBrowserCommand('navigate', { url }, sessionId);
    }

    /**
     * Click element in shadow browser
     */
    async clickSelector(sessionId, selector) {
        return this.sendShadowBrowserCommand('click_selector', { selector }, sessionId);
    }

    /**
     * Scroll shadow browser
     */
    async scroll(sessionId, options) {
        return this.sendShadowBrowserCommand('scroll', options, sessionId);
    }

    /**
     * Auto-scroll to load all infinite scroll content
     */
    async autoScrollAll(sessionId, maxScrolls = 50) {
        return this.sendShadowBrowserCommand('auto_scroll', { maxScrolls }, sessionId);
    }

    /**
     * Extract data from shadow browser page
     */
    async extractData(sessionId, config) {
        return this.sendShadowBrowserCommand('extract_data', { config }, sessionId);
    }

    /**
     * Get raw HTML from shadow browser
     */
    async extractHTML(sessionId, selector = 'body') {
        return this.sendShadowBrowserCommand('extract_html', { selector }, sessionId);
    }

    /**
     * Get page info from shadow browser
     */
    async getPageInfo(sessionId) {
        return this.sendShadowBrowserCommand('get_page_info', {}, sessionId);
    }

    /**
     * Check if element exists in shadow browser
     */
    async hasElement(sessionId, selector) {
        return this.sendShadowBrowserCommand('has_element', { selector }, sessionId);
    }

    /**
     * Type text into input field
     */
    async typeText(sessionId, selector, text) {
        return this.sendShadowBrowserCommand('type_text', { selector, text }, sessionId);
    }

    /**
     * Close shadow browser session
     */
    async closeSession(sessionId) {
        const result = await this.sendShadowBrowserCommand('close_session', {}, sessionId);
        if (this.activeSession === sessionId) {
            this.activeSession = null;
        }
        return result;
    }

    /**
     * Get active sessions
     */
    async getSessions() {
        return this.sendShadowBrowserCommand('get_sessions', {}, null);
    }

    /**
     * Cleanup all shadow browser sessions
     */
    async cleanup() {
        const result = await this.sendShadowBrowserCommand('cleanup', {}, null);
        this.activeSession = null;
        return result;
    }

    /**
     * Fetch raw content from URL (parsing done by caller)
     */
    async fetchContent(url) {
        return this.sendShadowBrowserCommand('fetch_content', { url }, null);
    }

    /**
     * Send shadow browser command and wait for response
     */
    sendShadowBrowserCommand(action, payload, sessionId) {
        const requestId = this.generateRequestId();

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(requestId);
                reject(new Error('Request timeout'));
            }, 60000);

            this.pendingRequests.set(requestId, { resolve, reject, timeout });

            this.sendMessage('shadow_browser', {
                requestId,
                sessionId,
                action,
                payload
            });
        });
    }

    handleShadowBrowserResponse(payload) {
        const { requestId, ...response } = payload;
        const pending = this.pendingRequests.get(requestId);

        if (pending) {
            clearTimeout(pending.timeout);
            this.pendingRequests.delete(requestId);

            if (response.error) {
                pending.reject(new Error(response.error));
            } else {
                pending.resolve(response);
            }
        }
    }

    handleShadowBrowserProgress(payload) {
        for (const callback of this.progressCallbacks) {
            try {
                callback(payload);
            } catch (e) {
                console.error('[BrowserExtension] Progress callback error:', e);
            }
        }
    }

    /**
     * Register progress callback for shadow browser operations
     */
    onProgress(callback) {
        this.progressCallbacks.add(callback);
        return () => this.progressCallbacks.delete(callback);
    }

    // ============================================
    // History Methods
    // ============================================

    /**
     * Dump browser history
     */
    async dumpHistory(options = {}) {
        return this.sendHistoryCommand('dump', options);
    }

    /**
     * Get history grouped by domain
     */
    async getHistoryByDomain(options = {}) {
        return this.sendHistoryCommand('by_domain', options);
    }

    /**
     * Get most visited sites
     */
    async getMostVisited(limit = 100, options = {}) {
        return this.sendHistoryCommand('most_visited', { limit, ...options });
    }

    /**
     * Search history
     */
    async searchHistory(query, maxResults = 1000) {
        return this.sendHistoryCommand('search', { query, maxResults });
    }

    /**
     * Get recent history (last N days)
     */
    async getRecentHistory(days = 7, options = {}) {
        return this.sendHistoryCommand('recent', { days, ...options });
    }

    /**
     * Get history for date range
     */
    async getHistoryForDateRange(startTime, endTime, options = {}) {
        return this.sendHistoryCommand('date_range', { startTime, endTime, ...options });
    }

    /**
     * Get categorized history
     */
    async getCategorizedHistory(options = {}) {
        return this.sendHistoryCommand('categorized', options);
    }

    /**
     * Get history statistics
     */
    async getHistoryStats(options = {}) {
        return this.sendHistoryCommand('stats', options);
    }

    /**
     * Get usage patterns
     */
    async getUsagePatterns(options = {}) {
        return this.sendHistoryCommand('patterns', options);
    }

    /**
     * Get search queries from Google
     */
    async getSearchQueries(options = {}) {
        return this.sendHistoryCommand('search_queries', options);
    }

    /**
     * Send history command and wait for response
     */
    sendHistoryCommand(action, payload) {
        const requestId = this.generateRequestId();

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(requestId);
                reject(new Error('Request timeout'));
            }, 30000);

            this.pendingRequests.set(requestId, { resolve, reject, timeout });

            this.sendMessage('history', {
                requestId,
                action,
                payload
            });
        });
    }

    handleHistoryResponse(payload) {
        const { requestId, result, error } = payload;
        const pending = this.pendingRequests.get(requestId);

        if (pending) {
            clearTimeout(pending.timeout);
            this.pendingRequests.delete(requestId);

            if (error) {
                pending.reject(new Error(error));
            } else {
                pending.resolve(result);
            }
        }
    }

    // ============================================
    // Config Methods
    // ============================================

    /**
     * Get all site monitoring configs
     */
    async getConfigs() {
        return this.sendConfigCommand('get_configs');
    }

    /**
     * Upsert a config
     */
    async upsertConfig(config) {
        return this.sendConfigCommand('upsert_config', { config });
    }

    /**
     * Delete a config
     */
    async deleteConfig(configId) {
        return this.sendConfigCommand('delete_config', { configId });
    }

    sendConfigCommand(action, payload = {}) {
        const requestId = this.generateRequestId();

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(requestId);
                reject(new Error('Request timeout'));
            }, 10000);

            this.pendingRequests.set(requestId, { resolve, reject, timeout });

            this.sendMessage(action, {
                requestId,
                ...payload
            });
        });
    }

    handleConfigsResponse(payload) {
        const { requestId, configs, manifest, error } = payload;
        const pending = this.pendingRequests.get(requestId);

        if (pending) {
            clearTimeout(pending.timeout);
            this.pendingRequests.delete(requestId);

            if (error) {
                pending.reject(new Error(error));
            } else {
                if (configs) this.configs = configs;
                if (manifest) this.configManifest = manifest;
                pending.resolve({ configs, manifest });
            }
        }
    }

    // ============================================
    // URL/Domain Block Methods
    // ============================================

    async addUrlBlock(payload) {
        return this._sendRequest('url_block_add', payload);
    }

    async removeUrlBlock(id) {
        return this._sendRequest('url_block_remove', { id });
    }

    async listUrlBlocks() {
        return this._sendRequest('url_block_list', {});
    }

    // ============================================
    // Utilities
    // ============================================

    generateRequestId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    }
}
